import type { Provider } from '@newfront-insurance/react-provision';
import { useProvider } from '@newfront-insurance/react-provision';
import { type ReactNode, type JSX } from 'react';
import * as React from 'react';

import type { AuthProviderProps } from '../providers/auth-provider';
import { useAuthClientLogin } from '../providers/auth-provider';
import type { AuthProviderContext } from '../types';

type Props = LoginBoundaryProps & {
  // eslint-disable-next-line react/no-unused-prop-types
  CustomLoginBoundary?: React.ComponentType<LoginBoundaryProps>;
};

export type LoginBoundaryProps = {
  fallback?: JSX.Element | null;
  children: ReactNode;
  authProvider: Provider<AuthProviderContext, AuthProviderProps>;
  // eslint-disable-next-line react/no-unused-prop-types
  isEnabled?: boolean;
};

export function LoginBoundary({
  isEnabled = true,
  CustomLoginBoundary = undefined,
  ...restProps
}: Props): JSX.Element | null {
  if (!isEnabled) {
    return restProps.children as JSX.Element;
  }

  if (CustomLoginBoundary) {
    return <CustomLoginBoundary {...restProps} />;
  }

  return <LoginBoundaryProvider {...restProps} />;
}

/**
 * Everything within this boundary will only be rendered if the user has completed the auth flow.
 */
export function LoginBoundaryProvider(props: Props): JSX.Element | null {
  const { fallback, children, authProvider } = props;
  const { authState, client } = useProvider(authProvider);
  const { isLoggedIn, isReady } = authState;

  // Automatically login
  useAuthClientLogin(client);

  if (!isReady || !isLoggedIn) {
    return fallback || null;
  }

  return children as JSX.Element;
}
